import { Component, inject, OnInit, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AppStateService } from './app-state.service';
import { ConfigService } from './config.service';
import { AuthService } from './auth.service';
import { DeeplinkService } from './deeplink.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { ThemeSwitcherComponent } from './core/components/theme-switcher/theme-switcher.component';
import { ThemeService, ThemeName, ThemeMode } from './core/services/theme.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet,
    CommonModule,
    MatProgressSpinnerModule,
    ThemeSwitcherComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'patient-portal-web';
  clientId: string | null = '';
  orgId: string | null = '';
  authService = inject(AuthService);
  router = inject(Router);
  loading = true;
  redirectUrl = '';
  deeplinkRoute = false;

  // Public routes that don't require authentication (for direct access)
  private publicRoutes = ['/privacy', '/terms', '/signup'];

  constructor(
    private appStateService: AppStateService,
    private route: ActivatedRoute,
    private configService: ConfigService,
    private themeService: ThemeService,
    private deeplinkService: DeeplinkService,
  ) {
    this.orgId = this.configService.getConfigData().org_id;
    this.clientId = this.configService.getConfigData().client_id;
    this.appStateService.setState({
      clientId: this.clientId,
      orgId: this.orgId,
      apiKey: this.configService.getConfigData().api_key,
      tenantId: this.configService.getConfigData().tenantId,
      patientId: sessionStorage.getItem('patientId'),
    });
  }

  ngOnInit(): void {
    // Store initial route for deep link context
    // Use window.location.pathname for reliable URL during bootstrap (router.url may not reflect actual URL yet)
    const initialUrl = window.location.pathname;
    const isDeepLink = !initialUrl.includes('/login') && initialUrl !== '/' && initialUrl !== ''
      && !this.isPublicRoute(initialUrl);

    // Subscribe to route params
    this.route.params.subscribe(params => {
    });

    // Subscribe to query params
    this.route.queryParams.subscribe(queryParams => {
      // Store deep link context if this is not a login/default route
      if (isDeepLink) {
        this.deeplinkService.storeDeeplinkContext(initialUrl, queryParams);
      }
      

      // Initialize theme from query parameters
      const theme = queryParams['theme'] as ThemeName;
      const mode = queryParams['mode'] as ThemeMode;
      if (theme || mode) {
        this.themeService.initializeFromConfig(theme, mode);
      }

      // Handle transparency/opacity parameter (0-100 scale, like web-assistant-app)
      const opacity = queryParams['opacity'];
      if (opacity) {
        const opacityValue = parseInt(opacity, 10);
        if (!isNaN(opacityValue) && opacityValue >= 0 && opacityValue <= 100) {
          // Convert 0-100 to 0-1 scale
          const opacityDecimal = opacityValue / 100;
          document.body.style.setProperty('--app-opacity', opacityDecimal.toString());
          document.body.style.setProperty('--app-bg-opacity', opacityDecimal.toString());
          document.body.classList.add('transparent-mode');
        }
      }

      // Handle blur parameter (0-20 scale for px, default 8)
      const blur = queryParams['blur'];
      if (blur !== undefined) {
        const blurValue = parseInt(blur, 10);
        if (!isNaN(blurValue) && blurValue >= 0 && blurValue <= 20) {
          document.body.style.setProperty('--app-blur', `${blurValue}px`);
        }
      }
      if (queryParams['link']) {
        this.redirectUrl = queryParams['link'];
      } else {
        // Check if there's existing deep link context from previous page load
        const existingDeepLink = this.deeplinkService.getDeeplinkContext();
        if (existingDeepLink && existingDeepLink.queryParams && existingDeepLink.queryParams.link) {
          this.redirectUrl = existingDeepLink.queryParams.link;
        }
      }
      
      if (this.redirectUrl) {
        // Check if the link is supported  - forms/<form_id>, consent/<consent_id>
        if (this.redirectUrl.startsWith('forms') ||
          this.redirectUrl.startsWith('consent') ||
          this.redirectUrl.startsWith('payment') ||
          this.redirectUrl.startsWith('payments') ||
          this.redirectUrl.startsWith('account-history') ||
          this.redirectUrl.startsWith('insurance') ||
          this.redirectUrl.startsWith('insurance_manage') ||
          this.redirectUrl === 'account_statement' ||
          this.redirectUrl === 'account_details') {

          // Store deep link context for these query parameter links
          const targetRoute = `/dashboard/${this.redirectUrl}`;
          // If we restored from existing context, use those queryParams, otherwise use current ones
          const contextQueryParams = queryParams['link'] ? queryParams : { link: this.redirectUrl };
          // Only store if we don't already have valid context or if this is from fresh query params
          if (queryParams['link'] || !this.deeplinkService.hasDeeplinkContext()) {
            this.deeplinkService.storeDeeplinkContext(targetRoute, contextQueryParams);
          } else {
          }
          this.appStateService.setState({ redirectUrl: this.redirectUrl });

          // Check if user is authenticated
          if (this.authService.isAuthenticated()) {
            // User is logged in, navigate to the form
            this.router.navigate(['/dashboard', ...this.redirectUrl.split('/')]);
          } else {
            // User not logged in, redirect to login
            // The deep link context will be preserved and used after login
            this.router.navigate(['/login']);
          }

        } else if (this.redirectUrl.startsWith('appointment/new')) {
          // anonymous login
          this.appStateService.setState({ redirectUrl: this.redirectUrl });
          this.loading = true;
          this.authService.loginAnonymous('', 'anonymous')
            .subscribe({
              next: (response: any) => {
                this.loading = false;
                this.authService.setToken(response.access_token);
                this.authService.setRefreshToken(response.refresh_token);
                this.appStateService.setState({
                  patientId: response.patient_id,
                });
              },
              error: (error) => {
                this.loading = false;
              }
            });
        } else {
          this.appStateService.setState({ redirectUrl: '/dashboard' });
        }
      }
      if (queryParams['link_id'] && queryParams['link_code'] && queryParams['action_type']) {
        const linkId = queryParams['link_id'];
        const linkCode = queryParams['link_code'];
        const actionType = queryParams['action_type'];
        if (linkId && linkCode && actionType) {
          this.deeplinkRoute = true;
          this.loading = true;
          this.authService.loginDeepLink(linkId, linkCode)
            .subscribe({
              next: (response: any) => {
                this.loading = false;
                this.authService.setToken(response.access_token);
                this.authService.setRefreshToken(response.refresh_token);
                const tokenInfo = response.token_info;
                this.appStateService.setState({
                  patientId: tokenInfo.patient_id,
                  clientId: tokenInfo.client_id,
                  orgId: tokenInfo.org_id,
                  redirectUrl: this.redirectUrl,
                  deeplinkData: {
                    linkId: linkId,
                    linkCode: linkCode,
                    actionType: actionType,
                    tokenInfo: response.token_info,
                  }
                });
                this.router.navigate(['/handle-deeplink']);
              },
              error: (error) => {
                this.loading = false;
              }
            });
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Ensure we navigate to login after capturing URL parameters
    setTimeout(() => {
      this.loading = false;
      if (this.deeplinkRoute) {
        return;
      }

      // Use window.location.pathname for reliable URL during bootstrap
      // (this.router.url may still be '/' if lazy-loaded route hasn't resolved)
      const currentUrl = window.location.pathname;

      // Public routes - no auth needed, stay on current page
      if (this.isPublicRoute(currentUrl)) {
        return;
      }

      // Check if there's deep link context and user is not authenticated
      const hasDeepLinkContext = this.deeplinkService.hasDeeplinkContext();
      if (hasDeepLinkContext && !this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
        return;
      }

      if (this.redirectUrl !== '' && this.redirectUrl.startsWith('appointment/new')) {
        // Store deep link context before navigating
        this.deeplinkService.storeDeeplinkContext('/new-patient-schedule', {});
        this.router.navigate(['/new-patient-schedule']);
      } else if (this.redirectUrl !== '') {
        // Navigate to the query parameter deep link route
        this.router.navigate(['/dashboard', ...this.redirectUrl.split('/')]);
      } else if (currentUrl === '/' || currentUrl === '') {
        // Only redirect from root path
        if (this.authService.isAuthenticated()) {
          this.router.navigate(['/dashboard']);
        } else {
          this.router.navigate(['/login']);
        }
      } else if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
      }
    }, 0);
  }

  private isPublicRoute(url: string): boolean {
    return this.publicRoutes.some(route => url.startsWith(route));
  }
}
